import {
	Component,
	EventEmitter,
	Input,
	Output,
	OnDestroy,
	ChangeDetectionStrategy,
	ElementRef,
	inject
} from '@angular/core';
import * as dayjs from 'dayjs';
import { LibraryTreeDataSource } from './library-tree-datasource';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
	LibraryCollection,
	LibraryCollectionItem,
	LibraryCollectionItemsAndFolders,
	LibraryFolder
} from '@go-modules/interfaces/library/library-collection';
import { ContentTypeType } from '@go-modules/interfaces/library/library-types';
import {
	CONTENT_TYPES,
	MODES,
	SORT_OPTIONS
} from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { MatTreeModule } from '@angular/material/tree';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { NgxMediaThumbnailModule } from '@go-modules/components/media-thumbnail/media-thumbnail.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxZeroStateModule } from '@go-modules/components/zero-state/zero-state.module';
import {
	CollectionFolderViewerModule
} from '@go-modules/components/library/collection-folder-viewer/collection-folder-viewer.module';
import {
	CollectionItemViewerModule
} from '@go-modules/components/library/collection-item-viewer/collection-item-viewer.module';
import { LibraryTreeNode, NodeType } from '@go-modules/components/library/library-tree-viewer/library-tree-node';
import { OrgSettings } from '@go-modules/interfaces/groups/org-settings';

@Component({
	selector: 'ngx-library-tree-viewer',
	templateUrl: './library-tree-viewer.component.html',
	styleUrls: ['./library-tree-viewer.component.scss'],
	imports: [
		MatTreeModule,
		MatMenuModule,
		MatIconModule,
		MatCheckboxModule,
		FormsModule,
		MatProgressBarModule,
		TranslatePipe,
		MatButtonModule,
		NgxMediaThumbnailModule,
		MatProgressSpinnerModule,
		NgxZeroStateModule,
		CollectionFolderViewerModule,
		CollectionItemViewerModule
	],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LibraryTreeViewerComponent implements OnDestroy {
	@Input() public collectionItems: LibraryCollectionItemsAndFolders;
	@Input() public selectedCollection: LibraryCollection;
	@Input() public selectedContentType: ContentTypeType;
	@Input() public collections: LibraryCollection[];
	@Input() public archiveCollection: LibraryCollection;
	@Input() public mode: string;
	@Input() public multiSelect: boolean = false;
	@Input() public sortBy: string = SORT_OPTIONS.LASTMODIFIED;
	@Input() public searchStr: string = '';
	@Input() public orgSettings: OrgSettings;
	@Input() public expandedHeight: number = 370;
	@Input() public currentlySelected: { id?: number; hash?: string; folderId?: number; };

	@Output() public selectItem = new EventEmitter<any>();
	@Output() public setCurrentlySelected = new EventEmitter<any>();
	@Output() public loadNewItems = new EventEmitter<any>();
	@Output() public setActivityPanelIsOpen = new EventEmitter<boolean>();
	@Output() public setSelectAllChecked = new EventEmitter<boolean>();

	public MODES = MODES;
	public CONTENT_TYPES = CONTENT_TYPES;
	public dataSource = inject(LibraryTreeDataSource);
	private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
	private translate = inject(TranslateService);

	public ngOnDestroy (): void {
		this.dataSource.disconnect();
	}

	public handleExpandedChange (node: LibraryTreeNode, expanded: boolean): void {
		if (expanded && !this.dataSource.isExpanded(node)) {
			this.dataSource.expand(node);
		} else if (!expanded && this.dataSource.isExpanded(node)) {
			this.dataSource.collapse(node);
		}
	}

	public isFolderEmpty (node: LibraryTreeNode): boolean {
		if (node.type !== 'folder' || node.isLoading) {
			return false;
		}

		if (!this.dataSource.isExpanded(node)) {
			return false;
		}

		const nodeIndex = this.dataSource.data.indexOf(node);
		if (nodeIndex === -1 || nodeIndex === this.dataSource.data.length - 1) {
			return true; // Last node or not found
		}

		// Check if next node is a child (has higher level)
		const nextNode = this.dataSource.data[nodeIndex + 1];
		return !nextNode || nextNode.level <= node.level;
	}

	public trackNode = (_index: number, node: LibraryTreeNode): string => {
		return node.key;
	};

	public getTreeAriaMultiselectable (): 'true' | 'false' {
		return this.multiSelect ? 'true' : 'false';
	}

	public getNodeElementId (node: LibraryTreeNode): string {
		return `library-tree-node-${node.key}`;
	}

	public getChildNodeIds (node: LibraryTreeNode): string | null {
		if (node.type !== NodeType.Folder) {
			return null;
		}

		const childIds: string[] = [];
		const startIndex = this.dataSource.data.findIndex((currentNode) => currentNode.key === node.key);
		if (startIndex === -1) {
			return null;
		}

		for (let index = startIndex + 1; index < this.dataSource.data.length; index++) {
			const currentNode = this.dataSource.data[index];
			if (currentNode.level <= node.level) {
				break;
			}

			if (currentNode.level === node.level + 1) {
				childIds.push(this.getNodeElementId(currentNode));
			}
		}

		return childIds.length > 0 ? childIds.join(' ') : null;
	}

	public getNodeAriaSelected (node: LibraryTreeNode): 'true' | 'false' | null {
		if (this.multiSelect) {
			return null;
		}

		return this.isNodeSelected(node) ? 'true' : 'false';
	}

	public getNodeAriaChecked (node: LibraryTreeNode): 'true' | 'false' | null {
		if (!this.multiSelect || node.type !== NodeType.Item) {
			return null;
		}

		return node.checked ? 'true' : 'false';
	}

	public getNodeAriaLabel (node: LibraryTreeNode): string | null {
		if (node.type !== NodeType.Folder) {
			return null;
		}

		const folder = node.data as LibraryFolder;
		return this.translate.instant('collection-folder-aria-label', {
			name: folder.name,
			date: dayjs(folder.updated_at).format('MMM D, YYYY')
		});
	}

	public handleNodeActivation (node: LibraryTreeNode): void {
		const selector = node.type === NodeType.Folder
			? `[data-tree-node-key="${node.key}"] .library-folder-info`
			: `[data-tree-node-key="${node.key}"] .library-item-info`;
		const target = this.elementRef.nativeElement.querySelector<HTMLElement>(selector);
		target?.click();
	}

	public handleSelectItem (event: any): void {
		this.selectItem.emit(event);
	}

	private isNodeSelected (node: LibraryTreeNode): boolean {
		if (node.type === NodeType.Folder) {
			return this.currentlySelected?.id === (node.data as LibraryFolder).id;
		}

		return this.currentlySelected?.hash === (node.data as LibraryCollectionItem).hash;
	}
}
